import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { colors, fonts } from '../brand';
import { API } from '../context/AuthContext';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import Disclaimer from '../components/Disclaimer';

export default function Refuge() {
  const [needIt, setNeedIt] = useState(null);
  const [wantIt, setWantIt] = useState(null);
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API}/api/refuge/interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ need_it: needIt, want_it: wantIt, city, state, email }),
      });
    } catch {}
    setSent(true);
    setLoading(false);
  };

  const canSubmit = needIt !== null && wantIt !== null && city.trim() && state.trim();

  return (
    <div style={styles.page}>
      <Helmet>
        <title>Refuge — Elephants Answering</title>
        <meta name="description" content="A coming-soon coliving space designed for women navigating divorce. Private, supportive, flexible. Help us understand the need and where to build first." />
      </Helmet>
      <SiteNav />

      <section style={styles.hero}>
        <div style={styles.heroInner}>
          <p style={styles.eyebrow}>COMING SOON</p>
          <h1 style={styles.title}>Refuge</h1>
          <p style={styles.tagline}>
            Where a hotel meets home — private bed and bath, shared spaces, thoughtfully designed for women navigating divorce.
          </p>
          <div style={styles.pillars}>
            {['Private.', 'Supportive.', 'Flexible.'].map(p => (
              <span key={p} style={styles.pillar}>{p}</span>
            ))}
          </div>
          <div style={styles.propertyBlock}>
            <a href="mailto:amy@elephantsanswering.com" style={styles.propertyBtn}>Offer a Property</a>
            <p style={styles.propertySub}>Have a home, land, or rental you'd like to put toward the mission? We'd love to talk.</p>
          </div>
        </div>
      </section>

      {/* Interest form */}
      <section style={styles.formSection}>
        <div style={styles.formInner}>
          <h2 style={styles.formTitle}>Help us understand the need.</h2>
          <p style={styles.formDesc}>Two questions and your city. That's it. Your answers help us figure out where to build first.</p>

          {sent ? (
            <div style={styles.thanks}>
              <p style={styles.thanksText}>Thank you. We'll let you know when Refuge comes to your area.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={styles.form}>

              <div style={styles.question}>
                <p style={styles.questionText}>Do you need it right now?</p>
                <div style={styles.options}>
                  <button type="button" style={{ ...styles.option, ...(needIt === true ? styles.optionSelected : {}) }} onClick={() => setNeedIt(true)}>Yes</button>
                  <button type="button" style={{ ...styles.option, ...(needIt === false ? styles.optionSelected : {}) }} onClick={() => setNeedIt(false)}>Not right now</button>
                </div>
              </div>

              <div style={styles.question}>
                <p style={styles.questionText}>Would you want it if it existed?</p>
                <div style={styles.options}>
                  <button type="button" style={{ ...styles.option, ...(wantIt === true ? styles.optionSelected : {}) }} onClick={() => setWantIt(true)}>Yes</button>
                  <button type="button" style={{ ...styles.option, ...(wantIt === false ? styles.optionSelected : {}) }} onClick={() => setWantIt(false)}>No</button>
                </div>
              </div>

              <div style={styles.locationRow}>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="City"
                  value={city}
                  onChange={e => setCity(e.target.value)}
                  required
                />
                <input
                  style={styles.input}
                  type="text"
                  placeholder="State"
                  value={state}
                  onChange={e => setState(e.target.value)}
                  required
                />
              </div>

              <input
                style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
                type="email"
                placeholder="Email (optional — if you'd like us to notify you)"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />

              <button style={{ ...styles.submitBtn, opacity: canSubmit ? 1 : 0.4 }} type="submit" disabled={!canSubmit || loading}>
                {loading ? 'Sending...' : 'Submit'}
              </button>
            </form>
          )}
        </div>
      </section>

      <Disclaimer />
      <SiteFooter />
    </div>
  );
}

const styles = {
  page: { fontFamily: fonts.body, backgroundColor: colors.cream, minHeight: '100vh', display: 'flex', flexDirection: 'column' },

  hero: { background: `linear-gradient(150deg, ${colors.navy} 0%, #003366 100%)`, padding: '100px 20px 80px', textAlign: 'center' },
  heroInner: { maxWidth: '640px', margin: '0 auto' },
  eyebrow: { fontFamily: fonts.body, color: colors.rose, fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '24px' },
  title: { fontFamily: fonts.heading, color: colors.white, fontSize: '52px', fontWeight: '600', letterSpacing: '1px', marginBottom: '32px' },
  tagline: { fontFamily: fonts.heading, color: colors.peach, fontSize: '18px', lineHeight: '1.9', fontStyle: 'italic', fontWeight: '400', marginBottom: '40px' },
  pillars: { display: 'flex', justifyContent: 'center', gap: '32px', flexWrap: 'wrap' },
  pillar: { fontFamily: fonts.heading, color: colors.white, fontSize: '16px', letterSpacing: '1px', fontWeight: '600' },
  propertyBlock: { marginTop: '48px', paddingTop: '40px', borderTop: '1px solid rgba(255,255,255,0.15)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' },
  propertyBtn: { display: 'inline-block', backgroundColor: colors.rose, color: colors.white, padding: '16px 48px', fontSize: '16px', fontFamily: fonts.body, fontWeight: '600', letterSpacing: '0.5px', textDecoration: 'none', cursor: 'pointer' },
  propertySub: { fontFamily: fonts.body, color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: '1.7', maxWidth: '420px', margin: 0 },

  formSection: { backgroundColor: colors.offWhite, padding: '64px 20px 80px', borderTop: `1px solid ${colors.peach}` },
  formInner: { maxWidth: '540px', margin: '0 auto' },
  formTitle: { fontFamily: fonts.heading, color: colors.navy, fontSize: '22px', fontWeight: '600', letterSpacing: '0.3px', marginBottom: '12px' },
  formDesc: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', lineHeight: '1.8', marginBottom: '36px', fontStyle: 'italic' },

  form: { display: 'flex', flexDirection: 'column', gap: '28px' },
  question: {},
  questionText: { fontFamily: fonts.body, color: colors.navy, fontSize: '15px', marginBottom: '12px', fontWeight: '500' },
  options: { display: 'flex', gap: '12px' },
  option: { padding: '10px 28px', backgroundColor: 'transparent', border: `1px solid ${colors.peach}`, color: colors.textMedium, fontSize: '13px', fontFamily: fonts.body, cursor: 'pointer', letterSpacing: '0.3px' },
  optionSelected: { backgroundColor: colors.rose, border: `1px solid ${colors.rose}`, color: colors.white, fontWeight: '600' },

  locationRow: { display: 'flex', gap: '12px' },
  input: { flex: 1, padding: '12px 16px', backgroundColor: colors.white, border: `1px solid ${colors.peach}`, color: colors.navy, fontSize: '14px', fontFamily: fonts.body, outline: 'none', borderRadius: 0 },

  submitBtn: { alignSelf: 'flex-start', padding: '12px 36px', backgroundColor: colors.rose, color: colors.white, border: 'none', fontSize: '14px', fontFamily: fonts.body, fontWeight: '600', cursor: 'pointer', letterSpacing: '0.5px' },

  thanks: { padding: '32px', border: `1px solid ${colors.peach}`, backgroundColor: colors.white },
  thanksText: { fontFamily: fonts.body, color: colors.navy, fontSize: '15px', lineHeight: '1.8', fontStyle: 'italic', margin: 0 },
};
